import React, { useState, useEffect } from 'react';

interface SupportTicketsProps {
  token: string;
  adminMode?: boolean;
}

const SupportTickets: React.FC<SupportTicketsProps> = ({ token, adminMode }) => {
  const [tickets, setTickets] = useState<any[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [replyMsg, setReplyMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const loadTickets = async () => {
    try {
      const res = await fetch('/api/tickets', { headers: { Authorization: 'Bearer ' + token } });
      if (res.ok) setTickets(await res.json());
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadTickets(); }, []);

  const createTicket = async () => {
    if (!subject || !message) return;
    setLoading(true);
    try {
      const res = await fetch('/api/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ subject, message })
      });
      if (res.ok) { setShowNew(false); setSubject(''); setMessage(''); loadTickets(); }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const sendReply = async () => {
    if (!replyMsg || !selectedTicket) return;
    setLoading(true);
    try {
      const res = await fetch('/api/tickets/' + selectedTicket.id + '/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ message: replyMsg })
      });
      if (res.ok) { setReplyMsg(''); loadTicketDetail(selectedTicket.id); }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadTicketDetail = async (id: string) => {
    try {
      const res = await fetch('/api/tickets/' + id, { headers: { Authorization: 'Bearer ' + token } });
      if (res.ok) setSelectedTicket(await res.json());
    } catch (err) { console.error(err); }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await fetch('/api/tickets/' + id + '/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ status })
      });
      loadTicketDetail(id);
      loadTickets();
    } catch (err) { console.error(err); }
  };

  const statusBadge = (s: string) => {
    const colors: any = { open: { bg: '#d1fae5', color: '#065f46', text: 'Open' }, awaiting_customer: { bg: '#fef3c7', color: '#92400e', text: 'Awaiting You' }, closed: { bg: '#f1f5f9', color: '#475569', text: 'Closed' } };
    if (adminMode) { colors.awaiting_customer = { bg: '#dbeafe', color: '#1e40af', text: 'Awaiting Customer' }; }
    const c = colors[s] || { bg: '#f1f5f9', color: '#475569', text: s };
    return <span style={{ background: c.bg, color: c.color, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{c.text}</span>;
  };

  if (selectedTicket) {
    return (
      <div>
        <button onClick={() => setSelectedTicket(null)} style={{ background: 'none', border: 'none', color: '#533afd', cursor: 'pointer', fontSize: '14px', marginBottom: '12px' }}>&larr; Back to tickets</button>
        <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e5edf5', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>{selectedTicket.subject}</h3>
            {statusBadge(selectedTicket.status)}
          </div>
          {adminMode && selectedTicket.user_name && <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>From: {selectedTicket.user_name} ({selectedTicket.user_email})</p>}
          <p style={{ fontSize: '14px', color: '#1e293b', background: '#f8fafc', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>{selectedTicket.message}</p>
          
          {selectedTicket.replies?.map((r: any, i: number) => (
            <div key={i} style={{ marginBottom: '12px', padding: '12px', background: r.is_admin ? '#eff6ff' : '#f8fafc', borderRadius: '6px', borderLeft: r.is_admin ? '3px solid #533afd' : '3px solid #10b981' }}>
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>{r.is_admin ? 'Support Team' : 'You'} &middot; {new Date(r.created_at).toLocaleString()}</div>
              <div style={{ fontSize: '14px' }}>{r.message}</div>
            </div>
          ))}

          {selectedTicket.status !== 'closed' && (
            <div style={{ marginTop: '16px' }}>
              <textarea value={replyMsg} onChange={e => setReplyMsg(e.target.value)} placeholder="Write a reply..." rows={3} style={{ width: '100%', padding: '10px', border: '1px solid #e5edf5', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button onClick={sendReply} disabled={loading || !replyMsg} style={{ padding: '8px 16px', background: '#533afd', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>{loading ? 'Sending...' : 'Send Reply'}</button>
                {adminMode && <button onClick={() => updateStatus(selectedTicket.id, 'closed')} style={{ padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Close Ticket</button>}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '13px', color: '#64748b' }}>{tickets.length} ticket(s)</div>
        {!adminMode && <button onClick={() => setShowNew(!showNew)} style={{ padding: '8px 16px', background: '#533afd', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>{showNew ? 'Cancel' : '+ New Ticket'}</button>}
      </div>

      {showNew && (
        <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e5edf5', padding: '16px', marginBottom: '16px' }}>
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject" style={{ width: '100%', padding: '10px', border: '1px solid #e5edf5', borderRadius: '6px', fontSize: '14px', marginBottom: '8px' }} />
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your issue..." rows={4} style={{ width: '100%', padding: '10px', border: '1px solid #e5edf5', borderRadius: '6px', fontSize: '14px', resize: 'vertical', marginBottom: '8px' }} />
          <button onClick={createTicket} disabled={loading || !subject || !message} style={{ padding: '8px 16px', background: '#533afd', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>{loading ? 'Sending...' : 'Submit Ticket'}</button>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>Your ticket will be sent to our support team and we'll get back to you via email.</div>
        </div>
      )}

      {tickets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>No tickets yet. {!adminMode && 'Click "+ New Ticket" to open one.'}</div>
      ) : (
        tickets.map(t => (
          <div key={t.id} onClick={() => { loadTicketDetail(t.id); }} style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e5edf5', padding: '14px', marginBottom: '8px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <strong style={{ fontSize: '14px' }}>{t.subject}</strong>
              {statusBadge(t.status)}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>{new Date(t.created_at).toLocaleString()}{adminMode && t.user_name ? ' \u00b7 ' + t.user_name : ''}</div>
          </div>
        ))
      )}
    </div>
  );
};

export default SupportTickets;
